import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { prettyFloatMoney } from '@/helpers/utils';
import { prettyDate } from '@/helpers/date';
import { FLEXIBILITY_STATUS_LABELS, FLEXIBILITY_STATUSES } from '@/helpers/flexibility.js';
import { MyTooltip } from '@/webkit/MyTooltip';
import { EMPTY_DASH, REFIN_STATUSES } from '@/helpers/constants';
import { ProgressBar } from '../ProgressBar';
import s from './index.module.scss';

export const LoanCard = ({ loan, canApply, onClick, onApply }) => {
  const { t } = useTranslation('loans');
  return (
    <div className={s.wrapper} onClick={onClick}>
      <div className={s.amount}>{prettyFloatMoney(loan.amounts?.total_loan_amount)}</div>
      <div className={s.table}>
        <div className={s.thead}>
          <div className={s.thead_col}>{t('loan_card.onboarding_date')}</div>
          <div className={s.thead_col}>{t('loan_card.repayment_progress')}</div>
          <div className={s.thead_col}>{t('loan_card.outstanding')}</div>
          <div className={s.thead_col}>{t('loan_card.fpg_option')}</div>
          <div className={s.thead_col}>{t('loan_card.payment_flexibility')}</div>
        </div>
        <div className={s.tbody}>
          <div className={s.tbody_col}>{prettyDate(loan.end_date)}</div>
          <div className={s.tbody_col}>
            <div style={{ padding: '0 4px' }}>
              <ProgressBar percent={loan.payment_progress} />
            </div>
          </div>
          <div className={s.tbody_col}>{prettyFloatMoney(loan.amounts?.outstanding)}</div>
          <div className={s.tbody_col}>{loan.selected_fpg?.title || EMPTY_DASH}</div>
          <div className={s.tbody_col}>
            <div className={s.flexibility}>
              {loan.flexibility_info.status === FLEXIBILITY_STATUSES.available ? (
                <>
                  <MyTooltip
                    theme="dark"
                    renderContent={() => (
                      <div className={classnames(s.flexibility_tooltip, 'f-small-regular')}>
                        {t('loan_card.payment_move_tooltip')}
                      </div>
                    )}
                    renderElement={() => (
                      <div className={s.flexibility_status}>
                        {FLEXIBILITY_STATUS_LABELS(t)[loan.flexibility_info.status]}
                      </div>
                    )}
                  />
                  <div className={s.flexibility_dot} />
                  <div className={s.flexibility_amount}>
                    {t('loan_card.amount_fee_per_shift', {
                      amount: prettyFloatMoney(loan.flexibility_info.reschedule_fee_amount),
                    })}
                  </div>
                </>
              ) : (
                FLEXIBILITY_STATUS_LABELS(t)[loan.flexibility_info.status]
              )}
            </div>
          </div>
        </div>
      </div>
      {[REFIN_STATUSES.available, REFIN_STATUSES.applied].includes(
        loan.refinancing_info.status,
      ) && (
        <div className={s.refin}>
          <div className={s.refin_content}>
            <div className={s.refin_title}>{t('loan_card.refinance_title')}</div>
            {REFIN_STATUSES.applied === loan.refinancing_info.status ? (
              <div className={s.refin_desc}>
                <Trans
                  ns="loans"
                  i18nKey="loan_card.refinance_accepted"
                  components={{ Bold: <b className="f-700" /> }}
                  values={{
                    date: `${prettyDate(loan.refinancing_info.fundraising_start_date, 'DD')}-${prettyDate(loan.refinancing_info.fundraising_end_date, 'DD')} ${prettyDate(loan.refinancing_info.fundraising_end_date, 'MMM YYYY')}`,
                  }}
                />
              </div>
            ) : (
              <div className={s.refin_desc}>
                <Trans
                  ns="loans"
                  i18nKey="loan_card.refinance_available"
                  values={{
                    percent: `${loan.refinancing_info.available_option_amount}%`,
                    untilDate: prettyDate(loan.refinancing_info.valid_until_date),
                  }}
                  components={{ Bold: <b /> }}
                />
              </div>
            )}
          </div>
          {loan.refinancing_info.status === REFIN_STATUSES.available && canApply && (
            <button
              type="button"
              className={s.refin_apply_btn}
              onClick={e => {
                e.stopPropagation();
                onApply();
              }}
            >
              {t('loan_card.apply_refinance')}
            </button>
          )}
          {loan.refinancing_info.status === REFIN_STATUSES.applied && (
            <div className={s.badge}>{t('loan_card.applied')}</div>
          )}
        </div>
      )}
    </div>
  );
};
